import { Edit, Send, SquareArrowUpRightIcon, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AlertBox from '@/components/AlertBox';
import { DDI_WORK_ORDER_STATUS_OPTIONS, Feature, SSN_SEARCH_DDI_TYPE } from '@/utils/constant';
import { shareDdiInvoiceEmail, useDdiSearchResult } from '@/services/dueDiligenceService';
import { useClientById } from '@/services/clients';
import { hasFeature } from '@/services/featureService';
import { isAdminOrSupervisor, isDueDiligenceTeamMember } from '@/services/userService';
import { useUserStore } from '@/stores/user';
import { displayError, displayUserFacingError } from '@/lib/error';
import { toast } from '@/components/ui/use-toast';
import type { DdiInvoiceResponse, DdiWorkOrderDetailResponse, DdiWorkOrderStatus } from '@/types/dueDiligence.types';

const iconClasses = 'h-[20px] w-[20px] cursor-pointer';

const statusClassMap: Record<DdiWorkOrderStatus, string> = {
  OPEN: 'bg-[#C0CBD9] text-[#0A2240]',
  PENDING_RESULTS: 'bg-[#FFEEBF] text-[#6C4A09]',
  ASSIGNED: 'bg-[#E1EAFB] text-[#0A2240]',
  AWAITING_CLIENT: 'bg-[#FBEDFF] text-[#551175]',
  REVIEW_AND_BILL: 'bg-[#551175] text-[#F5F5F5]',
  BILLED: 'bg-[#D5F4E3] text-[#1B8652]',
  CANCELED: 'bg-[#F5F5F5] text-[#969696]',
};

type Props = {
  workOrder: DdiWorkOrderDetailResponse;
  invoice: DdiInvoiceResponse | null | undefined;
  onClose: () => void;
};

const collectRecipientEmails = (...sources: (string | string[] | null | undefined)[]): string[] =>
  Array.from(
    new Set(
      sources
        .flatMap((item) => (Array.isArray(item) ? item : item ? [item] : []))
        .map((email) => email.trim())
        .filter(Boolean)
    )
  );

export const DDIWorkOrderSidePanelHeader = ({ workOrder, invoice, onClose }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const [isEmailing, setIsEmailing] = useState(false);
  const isEmailingRef = useRef(false);
  const [invoiceConfirmationDialog, setInvoiceConfirmationDialog] = useState(false);
  const [displayRecipientEmails, setDisplayRecipientEmails] = useState(false);
  const { summary, clientDetails, actionFlags, commonDetails, searchDetails } = workOrder;

  const status = summary.status;
  const statusLabel = DDI_WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;

  const primaryIdentifier = invoice?.invoiceNumber
    ? `Invoice #: ${invoice.invoiceNumber}`
    : `W/O #: ${summary.workOrderNumber}`;

  const hasShareFlags =
    hasFeature(Feature.DUE_DILIGENCE) && hasFeature(Feature.DUE_DILIGENCE_INVOICE_SHARE);
  const isInvoiceShareable = invoice?.status === 'CONFIRMED';
  const canShareInvoice = hasShareFlags && Boolean(invoice) && isInvoiceShareable;

  const shouldLoadClient = hasShareFlags && Boolean(clientDetails.clientId);
  const {
    data: clientData,
    isPending: isClientPending,
    isError: isClientError,
  } = useClientById(clientDetails.clientId, { enabled: shouldLoadClient });

  const isWaitingForClient = shouldLoadClient && isClientPending;
  const hasClientLoadFailed = shouldLoadClient && isClientError;

  const invoiceRecipientEmails = useMemo(
    () =>
      collectRecipientEmails(
        clientData?.arInvoices,
        clientData?.arInvoicesCc,
        clientDetails.contactEmail
      ),
    [clientData?.arInvoices, clientData?.arInvoicesCc, clientDetails.contactEmail]
  );

  const canCreateSearchResult = isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser);

  const canEditWorkOrder =
    actionFlags.canEditWorkOrder &&
    (isDueDiligenceTeamMember(loggedInUser) || isAdminOrSupervisor(loggedInUser));

  const isSsnSearch = clientDetails.ddiType === SSN_SEARCH_DDI_TYPE;

  const { data: existingSearchResult } = useDdiSearchResult(commonDetails.id);
  const hasExistingSearchResult = Boolean(existingSearchResult);
  const searchResultsActionLabel = hasExistingSearchResult ? 'Edit Search Results' : 'Add Search Results';
  const canManageSearchResult = isSsnSearch;

  const closeConfirmationDialog = () => {
    setInvoiceConfirmationDialog(false);
    setDisplayRecipientEmails(false);
  };

  const handleOpenEmailInvoice = () => {
    if (!invoice || !canShareInvoice || isEmailingRef.current || isWaitingForClient) return;
    if (hasClientLoadFailed) {
      displayUserFacingError('Failed to load recipient email addresses. Please try again.');
      return;
    }
    if (invoiceRecipientEmails.length === 0) {
      displayUserFacingError('No recipient email addresses found for this invoice.');
      return;
    }
    setInvoiceConfirmationDialog(true);
  };

  const handleConfirmEmailInvoice = async () => {
    if (!invoice || !canShareInvoice || isEmailingRef.current) return;
    if (hasClientLoadFailed) {
      displayUserFacingError('Failed to load recipient email addresses. Please try again.');
      closeConfirmationDialog();
      return;
    }
    if (invoiceRecipientEmails.length === 0) {
      displayUserFacingError('No recipient email addresses found for this invoice.');
      closeConfirmationDialog();
      return;
    }

    isEmailingRef.current = true;
    setIsEmailing(true);
    try {
      const { data, error } = await shareDdiInvoiceEmail(invoice.invoiceId);

      if (error) {
        displayError(error);
        closeConfirmationDialog();
        return;
      }

      toast({ description: data?.message ?? 'Invoice emailed successfully.', duration: 3000 });
      closeConfirmationDialog();
      await queryClient.invalidateQueries({ queryKey: ['ddi-invoice', commonDetails.id] });
    } finally {
      isEmailingRef.current = false;
      setIsEmailing(false);
    }
  };

  const clientInitials = (() => {
    const parts = clientDetails.clientName?.trim().split(/\s+/).filter(Boolean) ?? [];
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })();

  const workOrderAddress = [searchDetails.addressLine1, searchDetails.addressLine2]
    .filter(Boolean)
    .join(', ');
  const workOrderCityStateZip = [searchDetails.city, searchDetails.state, searchDetails.zipCode]
    .filter(Boolean)
    .join(', ');

  const hasAlternateAddress = [
    searchDetails.alternativeAddressLine1,
    searchDetails.alternativeAddressLine2,
    searchDetails.alternativeAddressCity,
    searchDetails.alternativeAddressState,
    searchDetails.alternativeAddressZipCode,
  ].some((part) => typeof part === 'string' && part.trim().length > 0);

  const alternateAddress = [searchDetails.alternativeAddressLine1, searchDetails.alternativeAddressLine2]
    .filter(Boolean)
    .join(', ');
  const alternateCityStateZip = [
    searchDetails.alternativeAddressCity,
    searchDetails.alternativeAddressState,
    searchDetails.alternativeAddressZipCode,
  ]
    .filter(Boolean)
    .join(', ');

  const isEmailInvoiceDisabled = !canShareInvoice || isEmailing || isWaitingForClient;

  return (
    <div className="flex flex-col gap-4 p-6 pb-0 sticky top-0 bg-white">
      <div className="flex flex-row justify-between">
        <span className="font-semibold text-xl">{primaryIdentifier}</span>
        <div className="flex flex-row gap-2 h-[32px] [&_div]:border-[1px] [&_div]:p-[6px] [&_div]:rounded-md [&_div]:justify-center">
          {hasShareFlags ? (
            <DropdownMenu>
              <DropdownMenuTrigger>
                <div>
                  <Send color="#676767" className={iconClasses} />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="ml-[-100px] min-w-[200px]">
                <DropdownMenuItem disabled={isEmailInvoiceDisabled}>
                  <div
                    className={`flex justify-between items-center gap-2 w-full text-[12px] font-[500] ${
                      !isEmailInvoiceDisabled ? 'text-primary' : 'text-placeholder cursor-not-allowed'
                    }`}
                    onClick={handleOpenEmailInvoice}
                  >
                    <span>Email Invoice</span>
                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}

          <DropdownMenu>
            <DropdownMenuTrigger>
              <div>
                <Edit color="#676767" className={iconClasses} />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="ml-[-100px] min-w-[180px]">
              {canEditWorkOrder && (
                <>
                  <DropdownMenuItem>
                    <div
                      className="flex justify-between items-center gap-2 w-full text-[12px] text-primary font-[500]"
                      onClick={() => navigate(`/due-diligence/${commonDetails.id}/update`)}
                    >
                      <span>Edit Work Order</span>
                      <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="h-[2px]" />
                </>
              )}
              {canCreateSearchResult && (
                <DropdownMenuItem disabled={!canManageSearchResult}>
                  <div
                    className={`flex justify-between items-center gap-2 w-full text-[12px] font-[500] ${
                      canManageSearchResult ? 'text-primary' : 'text-placeholder cursor-not-allowed'
                    }`}
                    onClick={() => {
                      if (!canManageSearchResult) return;
                      navigate(
                        hasExistingSearchResult
                          ? `/due-diligence/${commonDetails.id}/search-results/edit`
                          : `/due-diligence/${commonDetails.id}/search-results/create`
                      );
                    }}
                  >
                    <span>{searchResultsActionLabel}</span>
                    <SquareArrowUpRightIcon className="h-4 w-4" color="#0A2240" />
                  </div>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            tabIndex={0}
            role="button"
            aria-label="Close side panel"
            className="h-[32px] cursor-pointer"
          >
            <X color="#676767" className={iconClasses} />
          </div>
        </div>
      </div>

      <div className="flex flex-row justify-between align-middle">
        <div className="flex flex-row gap-2">
          <div className="bg-[#9747FF33] rounded-md text-normal font-semibold p-2 h-[48px] w-[48px] flex justify-center text-[#55288F] items-center">
            <span>{clientInitials}</span>
          </div>
          <div className="flex flex-col" style={{ overflowWrap: 'anywhere' }}>
            <span className="text-base font-medium">{clientDetails.clientName}</span>
            <span className="text-sm font-normal text-placeholder">#{clientDetails.clientCode}</span>
          </div>
        </div>

        <div
          className={`h-[20px] w-fit px-3 text-xs font-medium rounded-full flex items-center justify-center ${statusClassMap[status]}`}
        >
          {statusLabel}
        </div>
      </div>

      <div className="text-[#969696] font-[400] flex flex-col text-sm">
        <span style={{ overflowWrap: 'anywhere' }}>{workOrderAddress}</span>
        <span>{workOrderCityStateZip}</span>
        {hasAlternateAddress ? (
          <div className="mt-2 flex flex-col">
            <span className="text-xs font-medium text-[#676767]">Alternate Address</span>
            {alternateAddress ? (
              <span style={{ overflowWrap: 'anywhere' }}>{alternateAddress}</span>
            ) : null}
            {alternateCityStateZip ? <span>{alternateCityStateZip}</span> : null}
          </div>
        ) : null}
      </div>

      {commonDetails.cancellationReason && (
        <div className="mx-0 mb-2 rounded-md bg-[#F5F5F5] border border-[#FFCDD2] px-4 py-3 flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase tracking-wide">Cancelation Reason</span>
          <span className="text-sm">{commonDetails.cancellationReason}</span>
          {commonDetails.cancelledBy && (
            <span className="text-xs">
              By {commonDetails.cancelledBy.name}
              {commonDetails.cancelledAt ? ` · ${new Date(commonDetails.cancelledAt).toLocaleDateString()}` : ''}
            </span>
          )}
        </div>
      )}

      {invoiceConfirmationDialog && (
        <AlertBox
          title="Are you sure?"
          description={
            <>
              <span>Are you sure you want to send the invoice? </span>
              {displayRecipientEmails ? (
                <>
                  <span>{invoiceRecipientEmails.join(', ')}</span>{' '}
                  <span
                    onClick={() => setDisplayRecipientEmails(false)}
                    className="text-primary cursor-pointer"
                  >
                    Hide Recipients
                  </span>
                </>
              ) : (
                <span onClick={() => setDisplayRecipientEmails(true)} className="text-primary cursor-pointer">
                  View Recipients
                </span>
              )}
            </>
          }
          isLoading={isEmailing}
          open={invoiceConfirmationDialog}
          cancelBtnLabel="No"
          confirmBtnLabel="Yes"
          cancelBtnClick={closeConfirmationDialog}
          confirmBtnClick={() => {
            void handleConfirmEmailInvoice();
          }}
        />
      )}
    </div>
  );
};
